import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { BaseViewStore } from 'core/stores';
import { TrackingDashboardService } from 'common/data';

const WAREHOUSE_COLORS = [
	'#6b7a8d', '#e85d75', '#4a90d9', '#f5a623', '#50c7b0',
	'#9b59b6', '#e05e5e', '#3ec97f', '#e8a838', '#5b8ef0',
];

export const TRACKING_STATUS_KEYS = ['awaitingPickup', 'enRoute', 'onVehicleForDelivery', 'delivered', 'exception', 'total'];

const AUTO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;

// Local calendar date. toISOString() would shift a local-midnight Date back a day
// for any browser east of UTC, so every preset asked for the wrong day.
function toDateString(d) {
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getDateRange(preset, customFrom, customTo) {
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	if (preset === 'yesterday') {
		const yesterday = new Date(today);
		yesterday.setDate(yesterday.getDate() - 1);
		return { from: toDateString(yesterday), to: toDateString(yesterday) };
	}
	if (preset === 'last7days') {
		const from = new Date(today);
		from.setDate(from.getDate() - 6);
		return { from: toDateString(from), to: toDateString(today) };
	}
	// custom
	return { from: customFrom || '', to: customTo || '' };
}

class TrackingDashboardViewStore extends BaseViewStore {
	@observable allShipments = [];
	@observable warehouseColorMap = {};
	@observable selectedWarehouse = 'all';
	@observable selectedSeller = 'all';
	@observable activeStatus = 'total';
	@observable error = null;

	@observable datePreset = 'yesterday';
	@observable customDateFrom = '';
	@observable customDateTo = '';
	@observable selectedCarrier = 'all';

	@observable gridViewOpen = false;
	@observable gridShipments = [];
	@observable gridLoading = false;
	@observable gridError = null;

	dashboardService = null;
	refreshTimerId = null;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.dashboardService = new TrackingDashboardService(this.rootStore.app.baasic.apiClient);
		this.initialize();
		this.startAutoRefresh();
	}

	@computed
	get dateRange() {
		return getDateRange(this.datePreset, this.customDateFrom, this.customDateTo);
	}

	@computed
	get orgType() {
		return this.rootStore.applicationStore && this.rootStore.applicationStore.organizationType;
	}

	@computed
	get statusKeys() {
		return TRACKING_STATUS_KEYS;
	}

	@action.bound
	async initialize() {
		this.suspend();
		try {
			await this.loadShipmentsSummary();
		} catch (e) {
			runInAction(() => {
				this.error = 'Failed to load tracking data';
			});
		} finally {
			this.resume();
		}
	}

	@action.bound
	async loadShipmentsSummary() {
		try {
			const res = await this.dashboardService.getShipmentsSummary(this.dateRange);
			const shipments = res.data || [];
			runInAction(() => {
				this.error = null;
				this.allShipments = shipments;
				const map = {};
				[...new Map(shipments.filter(s => s.warehouseId).map(s => [s.warehouseId, s.warehouseName])).entries()]
					.sort(([, a], [, b]) => a.localeCompare(b))
					.forEach(([id], i) => {
						map[id] = WAREHOUSE_COLORS[(i + 1) % WAREHOUSE_COLORS.length];
					});
				this.warehouseColorMap = map;
			});
		} catch (e) {
			runInAction(() => {
				this.error = 'Failed to load tracking data';
			});
		}
	}

	// ── Filter bases (cross-dimensional, mirrors DashboardViewStore pattern) ──

	@computed
	get warehouseBasis() {
		return this.allShipments
			.filter(s => this.selectedSeller === 'all' || s.sellerId === this.selectedSeller)
			.filter(s => this.activeStatus === 'total' || s.trackingStatus === this.activeStatus)
			.filter(s => this.selectedCarrier === 'all' || s.carrier === this.selectedCarrier);
	}

	@computed
	get sellerBasis() {
		return this.allShipments
			.filter(s => this.selectedWarehouse === 'all' || s.warehouseId === this.selectedWarehouse)
			.filter(s => this.activeStatus === 'total' || s.trackingStatus === this.activeStatus)
			.filter(s => this.selectedCarrier === 'all' || s.carrier === this.selectedCarrier);
	}

	@computed
	get warehouses() {
		const map = {};
		this.warehouseBasis
			.filter(s => s.warehouseId)
			.forEach(s => {
				if (!map[s.warehouseId]) map[s.warehouseId] = { id: s.warehouseId, name: s.warehouseName, count: 0 };
				map[s.warehouseId].count++;
			});
		return Object.values(map).sort((a, b) => b.count - a.count);
	}

	@computed
	get sellers() {
		const map = {};
		this.sellerBasis.forEach(s => {
			if (!map[s.sellerId]) map[s.sellerId] = { id: s.sellerId, name: s.sellerName, count: 0 };
			map[s.sellerId].count++;
		});
		return Object.values(map).sort((a, b) => b.count - a.count);
	}

	@computed
	get carrierBasis() {
		return this.allShipments
			.filter(s => this.selectedSeller === 'all' || s.sellerId === this.selectedSeller)
			.filter(s => this.selectedWarehouse === 'all' || s.warehouseId === this.selectedWarehouse)
			.filter(s => this.activeStatus === 'total' || s.trackingStatus === this.activeStatus);
	}

	@computed
	get carrierItems() {
		const map = {};
		this.carrierBasis
			.filter(s => s.carrier != null)
			.forEach(s => {
				if (!map[s.carrier]) map[s.carrier] = { id: s.carrier, name: s.carrier, count: 0 };
				map[s.carrier].count++;
			});
		const items = Object.values(map).sort((a, b) => b.count - a.count);
		return [{ id: 'all', name: 'All', count: this.carrierBasis.length }, ...items];
	}

	// All shipments respecting all active filters
	@computed
	get filtered() {
		return this.allShipments
			.filter(s => this.selectedSeller === 'all' || s.sellerId === this.selectedSeller)
			.filter(s => this.selectedWarehouse === 'all' || s.warehouseId === this.selectedWarehouse)
			.filter(s => this.activeStatus === 'total' || s.trackingStatus === this.activeStatus)
			.filter(s => this.selectedCarrier === 'all' || s.carrier === this.selectedCarrier);
	}

	// Warehouse+seller filtered but no status filter — for counting all status buckets
	@computed
	get statusBase() {
		return this.allShipments
			.filter(s => this.selectedSeller === 'all' || s.sellerId === this.selectedSeller)
			.filter(s => this.selectedWarehouse === 'all' || s.warehouseId === this.selectedWarehouse)
			.filter(s => this.selectedCarrier === 'all' || s.carrier === this.selectedCarrier);
	}

	@computed
	get statusCounts() {
		return this.statusBase.reduce(
			(acc, s) => {
				const key = s.trackingStatus;
				return { ...acc, [key]: (acc[key] || 0) + 1, total: acc.total + 1 };
			},
			{ awaitingPickup: 0, enRoute: 0, onVehicleForDelivery: 0, delivered: 0, exception: 0, total: 0 }
		);
	}

	@computed
	get notMovingPercent() {
		const { awaitingPickup, total } = this.statusCounts;
		if (!total) return 0;
		return Math.round((awaitingPickup / total) * 100);
	}

	@computed
	get chartData() {
		const byWarehouse = {};
		this.filtered.forEach(s => {
			const key = s.warehouseId != null ? s.warehouseId : '__unassigned__';
			const name = s.warehouseName != null ? s.warehouseName : 'Unassigned';
			if (!byWarehouse[key]) {
				byWarehouse[key] = { warehouse: name, warehouseId: s.warehouseId, awaitingPickup: 0, enRoute: 0, onVehicleForDelivery: 0, delivered: 0, exception: 0, total: 0 };
			}
			if (s.trackingStatus in byWarehouse[key]) byWarehouse[key][s.trackingStatus]++;
			byWarehouse[key].total++;
		});

		return Object.values(byWarehouse)
			.sort((a, b) => a.warehouse.localeCompare(b.warehouse))
			.map(row => ({
				warehouse: row.warehouse,
				warehouseId: row.warehouseId,
				value: this.activeStatus === 'total' ? row.total : (row[this.activeStatus] || 0),
				...(this.activeStatus === 'total' && {
					segments: [
						{ status: 'awaitingPickup', value: row.awaitingPickup },
						{ status: 'enRoute', value: row.enRoute },
						{ status: 'onVehicleForDelivery', value: row.onVehicleForDelivery },
						{ status: 'delivered', value: row.delivered },
						{ status: 'exception', value: row.exception },
					],
				}),
			}));
	}

	// Per-warehouse % not moving for the right-column health panel.
	// Uses only seller + carrier filters so ALL warehouses are visible regardless of warehouse selection.
	@computed
	get warehouseNotMoving() {
		const byWarehouse = {};
		this.allShipments
			.filter(s => this.selectedSeller === 'all' || s.sellerId === this.selectedSeller)
			.filter(s => this.selectedCarrier === 'all' || s.carrier === this.selectedCarrier)
			.forEach(s => {
				const key = s.warehouseId != null ? s.warehouseId : '__unassigned__';
				if (!byWarehouse[key]) {
					byWarehouse[key] = { name: s.warehouseName != null ? s.warehouseName : 'Unassigned', total: 0, notMoving: 0 };
				}
				byWarehouse[key].total++;
				if (s.trackingStatus === 'awaitingPickup') {
					byWarehouse[key].notMoving++;
				}
			});

		return Object.values(byWarehouse)
			.map(w => ({ ...w, percent: w.total ? Math.round((w.notMoving / w.total) * 100) : 0 }))
			.filter(w => w.notMoving > 0)
			.sort((a, b) => b.percent - a.percent);
	}

	@computed
	get warehouseItems() {
		return [
			{ id: 'all', name: 'All', color: WAREHOUSE_COLORS[0], count: this.warehouseBasis.length },
			...this.warehouses.map(w => ({
				...w,
				color: this.warehouseColorMap[w.id] != null ? this.warehouseColorMap[w.id] : WAREHOUSE_COLORS[0],
			})),
		];
	}

	@computed
	get sellerItems() {
		return [{ id: 'all', name: 'All', count: this.sellerBasis.length }, ...this.sellers];
	}

	@computed
	get warehouseLabel() {
		if (this.selectedWarehouse === 'all') return 'All Warehouses';
		const match = this.warehouses.find(w => w.id === this.selectedWarehouse);
		return match && match.name != null ? match.name : 'All Warehouses';
	}

	@computed
	get sellerLabel() {
		if (this.selectedSeller === 'all') return 'All Clients';
		const match = this.sellers.find(s => s.id === this.selectedSeller);
		return match && match.name != null ? match.name : 'All Clients';
	}

	@computed
	get selectedWarehouseName() {
		if (this.selectedWarehouse === 'all') return 'all';
		const match = this.warehouses.find(w => w.id === this.selectedWarehouse);
		return match && match.name != null ? match.name : 'all';
	}

	// ── Actions ─────────────────────────────────────────────────────────────

	@action.bound
	setSelectedWarehouse(id) {
		this.selectedWarehouse = id;
	}

	@action.bound
	setSelectedSeller(id) {
		this.selectedSeller = id;
	}

	@action.bound
	setActiveStatus(key) {
		this.activeStatus = key;
	}

	@action.bound
	setSelectedCarrier(carrier) {
		this.selectedCarrier = carrier;
	}

	@action.bound
	setDatePreset(preset) {
		this.datePreset = preset;
		if (preset !== 'custom') {
			this.loadShipmentsSummary();
		}
	}

	@action.bound
	setCustomDateFrom(val) {
		this.customDateFrom = val;
	}

	@action.bound
	setCustomDateTo(val) {
		this.customDateTo = val;
	}

	@action.bound
	async applyCustomDateRange() {
		this.suspend();
		try {
			await this.loadShipmentsSummary();
		} finally {
			this.resume();
		}
	}

	// ── Auto refresh ─────────────────────────────────────────────────────────

	startAutoRefresh = () => {
		this.stopAutoRefresh();
		this.refreshTimerId = setInterval(() => {
			this.loadShipmentsSummary();
		}, AUTO_REFRESH_INTERVAL_MS);
	};

	stopAutoRefresh = () => {
		if (this.refreshTimerId) {
			clearInterval(this.refreshTimerId);
			this.refreshTimerId = null;
		}
	};

	// ── Grid view ────────────────────────────────────────────────────────────

	@action.bound
	openGridView() {
		this.gridViewOpen = true;
		this.gridLoading = false;
		this.gridError = null;
		this.gridShipments = this.filtered.slice();
		this.stopAutoRefresh();
	}

	@action.bound
	closeGridView() {
		this.gridViewOpen = false;
		this.startAutoRefresh();
	}

	exportGridToCsv = ({ showSeller, showWarehouse }) => {
		const csv = toCSV(this.gridShipments, showSeller, showWarehouse);
		downloadCSV(csv);
	};

	@action.bound
	destroy() {
		this.stopAutoRefresh();
	}
}

// ── CSV helpers ──────────────────────────────────────────────────────────────

const STATUS_LABELS = { awaitingPickup: 'Awaiting Pickup', enRoute: 'En Route', onVehicleForDelivery: 'On Vehicle for Delivery', delivered: 'Delivered', exception: 'Exception' };

function escapeCSV(val) {
	if (val == null) return '';
	const s = /^[=+\-@]/.test(String(val)) ? "'" + String(val) : String(val);
	if (s.includes(',') || s.includes('"') || s.includes('\n')) {
		return '"' + s.replace(/"/g, '""') + '"';
	}
	return s;
}

function toCSV(rows, showSeller, showWarehouse) {
	const cols = [
		{ header: 'Tracking #', get: o => o.trackingNumber },
		{ header: 'FO #', get: o => o.fulfillmentOrderId != null ? o.fulfillmentOrderId : '' },
		{ header: 'Carrier', get: o => o.carrier != null ? o.carrier : '' },
		{ header: 'Status', get: o => STATUS_LABELS[o.trackingStatus] || o.trackingStatus },
		showWarehouse ? { header: 'Warehouse', get: o => o.warehouseName != null ? o.warehouseName : '' } : null,
		showSeller ? { header: 'Client', get: o => o.sellerName != null ? o.sellerName : '' } : null,
		{ header: 'Last Update', get: o => o.lastUpdate ? String(o.lastUpdate).slice(0, 10) : '' },
		{ header: 'Label Created', get: o => { if (!o.labelCreatedDate) return ''; const d = new Date(o.labelCreatedDate); return isNaN(d.getTime()) ? '' : `${String(o.labelCreatedDate).slice(0, 10)} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`; } },
	].filter(Boolean);

	const header = cols.map(c => escapeCSV(c.header)).join(',');
	const lines = rows.map(o => cols.map(c => escapeCSV(c.get(o))).join(','));
	return [header, ...lines].join('\r\n');
}

function downloadCSV(csv) {
	const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = `tracking-${new Date().toISOString().slice(0, 10)}.csv`;
	a.click();
	URL.revokeObjectURL(url);
}

export default TrackingDashboardViewStore;